import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  HeartCrack, 
  Lightbulb, 
  Shield, 
  Brain,
  ArrowRight,
  CheckCircle2,
  Lock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  'heart-crack': HeartCrack,
  'lightbulb': Lightbulb,
  'shield': Shield,
  'brain': Brain
};

const colorMap = {
  'trauma_bond': 'from-rose-400 to-rose-600',
  'gaslighting': 'from-purple-400 to-purple-600',
  'boundary': 'from-emerald-400 to-emerald-600',
  'nervous_system': 'from-blue-400 to-blue-600'
};

export default function Modules() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const res = await axios.get(`${API}/modules`);
      setModules(res.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="modules-page">
        {/* Header */}
        <div className="mb-10">
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-foreground mb-3">
            Healing Modules
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            Four comprehensive tracks designed to help you recover, rebuild, and reclaim your peace. 
            Work through each module at your own pace.
          </p>
        </div>

        {/* Progress Overview */}
        <div className="crown-card p-6 mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-foreground">Overall Progress</h2>
            <span className="text-primary font-bold">
              {modules.reduce((acc, m) => acc + m.completed_lessons, 0)} / {modules.reduce((acc, m) => acc + m.total_lessons, 0)} lessons
            </span>
          </div>
          <Progress 
            value={(modules.reduce((acc, m) => acc + m.completed_lessons, 0) / modules.reduce((acc, m) => acc + m.total_lessons, 0)) * 100} 
            className="h-3"
          />
        </div>

        {/* Modules Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {modules.map((module, idx) => {
            const Icon = iconMap[module.icon] || Shield;
            const colorClass = colorMap[module.id] || 'from-purple-400 to-purple-600';
            const isComplete = module.progress === 1;

            return (
              <Link 
                key={module.id} 
                to={`/modules/${module.id}`}
                data-testid={`module-${module.id}`}
                className="block"
              >
                <div className={`crown-card p-6 h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
                  isComplete ? 'border-green-300 dark:border-green-700' : 'hover:border-primary/30'
                }`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colorClass} flex items-center justify-center`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    {isComplete && (
                      <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm">
                        <CheckCircle2 className="w-4 h-4" />
                        Complete
                      </div>
                    )}
                  </div>
                  
                  <h3 className="font-serif text-xl font-semibold text-foreground mb-2">
                    {module.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-6">
                    {module.description}
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {module.completed_lessons} of {module.total_lessons} lessons
                      </span>
                      <span className="font-semibold text-primary">
                        {Math.round(module.progress * 100)}%
                      </span>
                    </div>
                    <Progress value={module.progress * 100} className="h-2" />
                  </div>
                  
                  <div className="mt-6 flex items-center text-primary font-medium group">
                    {isComplete ? 'Review Module' : 'Continue Learning'}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Info Note */}
        <div className="mt-10 p-6 rounded-2xl bg-purple-50 dark:bg-purple-950/30 border border-purple-100 dark:border-purple-800/30">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Recovery takes time.</strong> These modules are designed 
            to be worked through at your own pace. There's no rush. Some lessons may be harder than 
            others—that's normal. Take breaks when you need them, and remember: you're not alone in this.
          </p>
        </div>
      </div>
    </Layout>
  );
}
